import { useEffect, useMemo, useState } from 'react';
import Reveal from './Reveal';

function PartnershipApplicationForm({ app }) {
  const [categoryIndex, setCategoryIndex] = useState(null);
  const [basic, setBasic] = useState({
    fullName: '',
    country: '',
    city: '',
    whatsapp: '',
    email: '',
  });
  const [professional, setProfessional] = useState({
    license: '',
    portfolio: '',
    impact: '',
  });
  const [interests, setInterests] = useState(() => app.interestOptions.map(() => false));
  const [motivation, setMotivation] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [step, setStep] = useState(0);

  const showProfessionalStep = categoryIndex !== null;

  const steps = useMemo(() => {
    const base = ['category', 'basic'];
    if (showProfessionalStep) base.push('professional');
    base.push('interests', 'motivation', 'confirmation');
    return base;
  }, [showProfessionalStep]);

  const totalSteps = steps.length;

  useEffect(() => {
    setStep((current) => Math.min(current, Math.max(totalSteps - 1, 0)));
  }, [totalSteps]);

  const stepKey = steps[step] || 'category';

  const canContinue = useMemo(() => {
    switch (stepKey) {
      case 'category':
        return categoryIndex !== null;
      case 'basic':
        return (
          basic.fullName.trim() &&
          basic.country.trim() &&
          basic.city.trim() &&
          basic.whatsapp.trim() &&
          basic.email.trim()
        );
      case 'professional':
        return (
          professional.license.trim() &&
          professional.portfolio.trim() &&
          professional.impact.trim()
        );
      case 'interests':
        return interests.some(Boolean);
      case 'motivation':
        return motivation.trim().length >= 12;
      case 'confirmation':
        return confirmed;
      default:
        return false;
    }
  }, [stepKey, categoryIndex, basic, professional, interests, motivation, confirmed]);

  const stepProgressLabel = app.stepProgress
    .replace('{current}', String(step + 1))
    .replace('{total}', String(totalSteps));

  const goNext = () => {
    if (!canContinue) return;
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="application-grid">
      <Reveal>
        <div className="application-intro">
          <span className="eyebrow">{app.title}</span>
          <h2 className="clubs-title">{app.subtitle}</h2>
          <p className="application-note">{app.intro}</p>
        </div>
      </Reveal>

      <Reveal delay={120}>
        <form className="application-form" onSubmit={(e) => e.preventDefault()}>
          <div className="application-step-meta" aria-live="polite">
            <span className="application-step-progress">{stepProgressLabel}</span>
          </div>

          {stepKey === 'category' ? (
            <fieldset className="application-fieldset">
              <legend>{app.sections.category}</legend>
              <div className="application-check-grid">
                {app.categoryOptions.map((option, idx) => (
                  <label key={option} className="application-check">
                    <input
                      type="radio"
                      name="partner-category"
                      checked={categoryIndex === idx}
                      onChange={() => setCategoryIndex(idx)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {stepKey === 'basic' ? (
            <fieldset className="application-fieldset">
              <legend>{app.sections.basic}</legend>
              <label>
                <span>{app.basicFields.fullName}</span>
                <input
                  type="text"
                  value={basic.fullName}
                  onChange={(e) => setBasic((s) => ({ ...s, fullName: e.target.value }))}
                  placeholder={app.basicFields.fullName}
                />
              </label>
              <label>
                <span>{app.basicFields.country}</span>
                <input
                  type="text"
                  value={basic.country}
                  onChange={(e) => setBasic((s) => ({ ...s, country: e.target.value }))}
                  placeholder={app.basicFields.country}
                />
              </label>
              <label>
                <span>{app.basicFields.city}</span>
                <input
                  type="text"
                  value={basic.city}
                  onChange={(e) => setBasic((s) => ({ ...s, city: e.target.value }))}
                  placeholder={app.basicFields.city}
                />
              </label>
              <label>
                <span>{app.basicFields.whatsapp}</span>
                <input
                  type="text"
                  value={basic.whatsapp}
                  onChange={(e) => setBasic((s) => ({ ...s, whatsapp: e.target.value }))}
                  placeholder={app.basicFields.whatsapp}
                />
              </label>
              <label>
                <span>{app.basicFields.email}</span>
                <input
                  type="email"
                  value={basic.email}
                  onChange={(e) => setBasic((s) => ({ ...s, email: e.target.value }))}
                  placeholder={app.basicFields.email}
                />
              </label>
            </fieldset>
          ) : null}

          {stepKey === 'professional' ? (
            <fieldset className="application-fieldset">
              <legend>{app.sections.professional}</legend>
              <label>
                <span>{app.professionalFields.license}</span>
                <input
                  type="text"
                  value={professional.license}
                  onChange={(e) => setProfessional((s) => ({ ...s, license: e.target.value }))}
                  placeholder={app.professionalFields.license}
                />
              </label>
              <label>
                <span>{app.professionalFields.portfolio}</span>
                <input
                  type="text"
                  value={professional.portfolio}
                  onChange={(e) => setProfessional((s) => ({ ...s, portfolio: e.target.value }))}
                  placeholder={app.professionalFields.portfolio}
                />
              </label>
              <label>
                <span>{app.professionalFields.impact}</span>
                <input
                  type="text"
                  value={professional.impact}
                  onChange={(e) => setProfessional((s) => ({ ...s, impact: e.target.value }))}
                  placeholder={app.professionalFields.impact}
                />
              </label>
            </fieldset>
          ) : null}

          {stepKey === 'interests' ? (
            <fieldset className="application-fieldset">
              <legend>{app.sections.interests}</legend>
              <p className="application-prompt">{app.interestPrompt}</p>
              <div className="application-check-grid">
                {app.interestOptions.map((option, idx) => (
                  <label key={option} className="application-check">
                    <input
                      type="checkbox"
                      checked={Boolean(interests[idx])}
                      onChange={() =>
                        setInterests((prev) => prev.map((v, i) => (i === idx ? !v : v)))
                      }
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {stepKey === 'motivation' ? (
            <fieldset className="application-fieldset">
              <legend>{app.sections.motivation}</legend>
              <textarea
                rows="4"
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                placeholder={app.motivationPlaceholder}
              />
            </fieldset>
          ) : null}

          {stepKey === 'confirmation' ? (
            <fieldset className="application-fieldset">
              <legend>{app.sections.confirmation}</legend>
              <label className="application-check">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span>{app.confirmationLabel}</span>
              </label>
            </fieldset>
          ) : null}

          <div className="application-step-actions">
            <button type="button" className="ghost-btn" onClick={goBack} disabled={step === 0}>
              {app.back}
            </button>

            {step < totalSteps - 1 ? (
              <button type="button" className="primary-btn" onClick={goNext} disabled={!canContinue}>
                {app.next}
              </button>
            ) : (
              <button type="submit" className="primary-btn application-submit" disabled={!canContinue}>
                {app.submit}
              </button>
            )}
          </div>
        </form>
      </Reveal>
    </div>
  );
}

export default PartnershipApplicationForm;
